<script setup>
import { ref } from 'vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import DefaultLayout from '@/layouts/default.vue';
import DocumentTypesTreeTable from '@/components/documentTypes/DocumentTypesTreeTable.vue';
import ItemTypeFormDialog from '@/components/documentTypes/ItemTypeFormDialog.vue';
import DocumentTypePermissionsDialog from '@/components/documentTypes/DocumentTypePermissionsDialog.vue';
import { useDocumentTypesStore } from '@/stores/documentTypes.js';
import { useToast } from 'primevue';

const documentTypesStore = useDocumentTypesStore();
const toast = useToast();

const showAddDialog = ref(false);
const showEditDialog = ref(false);
const showPermissionsDialog = ref(false);
const addEntityType = ref('document'); // 'document' | 'file'
const editEntityType = ref('document'); // 'document' | 'file'
const permissionsEntityType = ref('document'); // 'document' | 'file'
const selectedItem = ref(null);
const selectedItemForPermissions = ref(null);

// Универсальная функция для обработки ошибок API
function getErrorMessage(error, defaultMessage = 'Произошла ошибка') {
  const data = error?.response?.data;

  // Если есть поле detail - показываем его (обычно человекочитаемое сообщение)
  if (data?.detail) {
    return data.detail;
  }

  // Если есть другие поля ошибок - форматируем их
  if (data && typeof data === 'object') {
    return Object.entries(data)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
      .join('; ');
  }

  return defaultMessage;
}

// === Обработчики для создания ===
function openAddDocumentDialog() {
  addEntityType.value = 'document';
  showAddDialog.value = true;
}

function openAddFileDialog() {
  addEntityType.value = 'file';
  showAddDialog.value = true;
}

async function handleCreate(itemData) {

  try {
    if (addEntityType.value === 'document') {
      const created = await documentTypesStore.createDocumentType(itemData);
    } else {
      const created = await documentTypesStore.createFileType(itemData);
    }

    showAddDialog.value = false;

    const typeName = addEntityType.value === 'document' ? 'Тип документа' : 'Тип файла';
    toast.add({
      severity: 'success',
      summary: `${typeName} создан`,
      detail: `${typeName} успешно создан`,
      life: 2500,
    });
  } catch (error) {
    console.error('[DocumentTypes] Create error:', error);

    toast.add({
      severity: 'error',
      summary: 'Ошибка создания',
      detail: getErrorMessage(error, 'Не удалось создать тип'),
      life: 4000,
    });
  }
}

// === Обработчики для редактирования ===
function handleEditDocument(document) {
  editEntityType.value = 'document';
  selectedItem.value = document;
  showEditDialog.value = true;
}

function handleEditFile(file) {
  editEntityType.value = 'file';
  selectedItem.value = file;
  showEditDialog.value = true;
}

async function handleUpdate(itemData) {

  if (!selectedItem.value || !selectedItem.value.id) {
    console.error('[DocumentTypes] No selected item or missing ID');
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось определить элемент для обновления',
      life: 4000,
    });
    return;
  }

  try {
    if (editEntityType.value === 'document') {
      await documentTypesStore.updateDocumentType(selectedItem.value.id, itemData);
    } else {
      await documentTypesStore.updateFileType(selectedItem.value.id, itemData);
    }

    showEditDialog.value = false;
    selectedItem.value = null;

    const typeName = editEntityType.value === 'document' ? 'Тип документа' : 'Тип файла';
    toast.add({
      severity: 'success',
      summary: `${typeName} обновлен`,
      detail: `${typeName} успешно обновлен`,
      life: 2500,
    });
  } catch (error) {
    console.error('[DocumentTypes] Update error:', error);

    toast.add({
      severity: 'error',
      summary: 'Ошибка обновления',
      detail: getErrorMessage(error, 'Не удалось обновить тип'),
      life: 4000,
    });
  }
}

// === Обработчики для удаления ===
async function handleDeleteDocument(document) {

  if (!document || !document.id) {
    console.error('[DocumentTypes] No document or missing ID');
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось определить элемент для удаления',
      life: 4000,
    });
    return;
  }

  try {
    await documentTypesStore.deleteDocumentType(document.id);
    toast.add({
      severity: 'success',
      summary: 'Тип документа удален',
      detail: 'Тип документа успешно удален',
      life: 2500,
    });
  } catch (error) {
    console.error('[DocumentTypes] Delete document error:', error);

    toast.add({
      severity: 'error',
      summary: 'Ошибка удаления',
      detail: getErrorMessage(error, 'Не удалось удалить тип документа'),
      life: 4000,
    });
  }
}

async function handleDeleteFile(file) {

  if (!file || !file.id) {
    console.error('[DocumentTypes] No file or missing ID');
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось определить элемент для удаления',
      life: 4000,
    });
    return;
  }

  try {
    await documentTypesStore.deleteFileType(file.id);
    toast.add({
      severity: 'success',
      summary: 'Тип файла удален',
      detail: 'Тип файла успешно удален',
      life: 2500,
    });
  } catch (error) {
    console.error('[DocumentTypes] Delete file error:', error);

    toast.add({
      severity: 'error',
      summary: 'Ошибка удаления',
      detail: getErrorMessage(error, 'Не удалось удалить тип файла'),
      life: 4000,
    });
  }
}

function handleCloseEditDialog() {
  showEditDialog.value = false;
  selectedItem.value = null;
}

// === Обработчики для прав доступа ===
function handleConfigurePermissions(item) {

  if (!item || !item.id) {
    console.error('[DocumentTypes] No item or missing ID');
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось определить элемент для настройки прав',
      life: 4000,
    });
    return;
  }

  selectedItemForPermissions.value = item;
  permissionsEntityType.value = item.entity_type;
  showPermissionsDialog.value = true;
}

async function handlePermissionsSave() {
  toast.add({
    severity: 'success',
    summary: 'Права сохранены',
    detail: 'Права доступа успешно обновлены',
    life: 2500,
  });
}

function handleClosePermissionsDialog() {
  showPermissionsDialog.value = false;
  selectedItemForPermissions.value = null;
}
</script>

<template>
  <DefaultLayout>
    <Card class="w-full flex-1 flex flex-col min-h-0"
          :pt="{
            body: { class: 'flex-1 flex flex-col h-full' },
            content: { class: 'p-0 flex-1 flex flex-col min-h-0' }
          }"
    >
      <template #title>
        <div class="flex justify-end gap-2">
          <Button
            label="Тип документа"
            icon="pi pi-plus"
            severity="info"
            rounded
            @click="openAddDocumentDialog"
          />
          <Button
            label="Тип файла"
            icon="pi pi-plus"
            severity="info"
            rounded
            @click="openAddFileDialog"
          />
        </div>
      </template>

      <template #content>
        <DocumentTypesTreeTable
          @edit-document="handleEditDocument"
          @edit-file="handleEditFile"
          @delete-document="handleDeleteDocument"
          @delete-file="handleDeleteFile"
          @configure-permissions="handleConfigurePermissions"
        />
      </template>
    </Card>

    <!-- Диалог создания -->
    <ItemTypeFormDialog
      v-model:visible="showAddDialog"
      mode="create"
      :entity-type="addEntityType"
      @save="handleCreate"
    />

    <!-- Диалог редактирования -->
    <ItemTypeFormDialog
      v-model:visible="showEditDialog"
      mode="edit"
      :entity-type="editEntityType"
      :item-type="selectedItem"
      @save="handleUpdate"
      @close="handleCloseEditDialog"
    />

    <!-- Диалог настройки прав -->
    <DocumentTypePermissionsDialog
      v-model:visible="showPermissionsDialog"
      :item-type="selectedItemForPermissions"
      :entity-type="permissionsEntityType"
      @save="handlePermissionsSave"
      @close="handleClosePermissionsDialog"
    />
  </DefaultLayout>
</template>

<style scoped>
</style>
